import Constant from "../util/constant";

/** Strip `Bearer` prefix and invalid storage placeholders. */
export function stripBearerPrefix(token) {
  if (token == null) return "";
  let t = String(token).trim();
  if (!t || t === "undefined" || t === "null") return "";
  while (t.startsWith("Bearer ")) {
    t = t.slice(7).trim();
  }
  return t;
}

/** Loose JWT shape check (three base64url segments). */
export function isLikelyJwt(token) {
  const raw = stripBearerPrefix(token);
  return /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(raw);
}

/** Read access token from localStorage, normalized (no Bearer prefix). */
export function readStoredAccessToken() {
  try {
    return stripBearerPrefix(
      window.localStorage.getItem(Constant.LOCALSTORAGEKEYS.ACCESSTOKEN)
    );
  } catch {
    return "";
  }
}

/**
 * Build Authorization header value. Backend expects `Bearer <jwt>`.
 * When REACT_APP_AUTHORIZATION_BEARER=false, send raw JWT only (legacy).
 */
export function buildAuthorizationHeader(token) {
  const raw = stripBearerPrefix(token);
  if (!raw || !isLikelyJwt(raw)) return null;
  if (process.env.REACT_APP_AUTHORIZATION_BEARER === "false") {
    return raw;
  }
  return `Bearer ${raw}`;
}

/** Attach Authorization on axios 1.x configs (always overwrite stale/empty values). */
export function applyAuthorizationHeader(config, authorization) {
  if (!authorization || !config) return config;
  const headers = config.headers;
  if (headers && typeof headers.set === "function") {
    headers.set("Authorization", authorization);
  } else {
    config.headers = { ...(headers || {}), Authorization: authorization };
  }
  return config;
}

/** Pick access token from login API payload (handles nested / alternate keys). */
export function pickAccessTokenFromLoginPayload(payload) {
  if (payload == null) return "";
  const root = typeof payload === "object" ? payload : {};
  const data =
    root.data && typeof root.data === "object" ? root.data : root;
  const tokens =
    data.tokens && typeof data.tokens === "object" ? data.tokens : null;

  const nestedAccess =
    data.accessToken && typeof data.accessToken === "object"
      ? data.accessToken.token ?? data.accessToken.accessToken
      : null;

  const candidates = [
    nestedAccess,
    data.accessToken,
    data.access_token,
    data.token,
    data.jwt,
    tokens?.accessToken,
    tokens?.access_token,
    tokens?.token,
    root.accessToken,
    root.access_token,
    root.token,
    data.user?.accessToken,
    data.user?.token,
  ];

  for (let i = 0; i < candidates.length; i += 1) {
    const normalized = stripBearerPrefix(candidates[i]);
    if (isLikelyJwt(normalized)) return normalized;
  }

  return stripBearerPrefix(
    data.accessToken ?? data.access_token ?? data.token ?? root.accessToken ?? ""
  );
}

export function pickRefreshTokenFromLoginPayload(payload) {
  if (payload == null) return "";
  const root = typeof payload === "object" ? payload : {};
  const data =
    root.data && typeof root.data === "object" ? root.data : root;
  const tokens =
    data.tokens && typeof data.tokens === "object" ? data.tokens : null;
  const raw =
    data.refreshToken ??
    data.refresh_token ??
    tokens?.refreshToken ??
    tokens?.refresh_token ??
    root.refreshToken ??
    "";
  const normalized = stripBearerPrefix(raw);
  return normalized && normalized !== "undefined" ? normalized : "";
}
